import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CustomLoader from '@common/components/Feedback/CustomLoader';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { useAppSelector } from 'store';
import { selectUser } from 'store/general/general-slice-selectors';
import { Language } from 'models/enums/languages';

const markdownStyles = {
  '& h1': { fontSize: '1.6rem', fontWeight: 800, textTransform: 'uppercase', mt: 0, mb: 2 },
  '& h2': { fontSize: '1.15rem', fontWeight: 800, textTransform: 'uppercase', mt: 4, mb: 1.5 },
  '& h3': { fontSize: '1rem', fontWeight: 700, textTransform: 'uppercase', mt: 3, mb: 1 },
  '& p': { mb: 1.5, lineHeight: 1.75, fontSize: '0.95rem' },
  '& ul': { pl: 2.5, mb: 2 },
  '& ol': { pl: 2.5, mb: 2 },
  '& li': { mb: 1, lineHeight: 1.7, fontSize: '0.95rem' },
  '& a': {
    color: 'primary.main',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  '& table': { width: '100%', minWidth: 480, borderCollapse: 'collapse', fontSize: '0.9rem' },
  '& th': {
    textAlign: 'left',
    fontWeight: 700,
    p: 1.25,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'action.hover',
  },
  '& td': {
    p: 1.25,
    border: '1px solid',
    borderColor: 'divider',
    verticalAlign: 'top',
    overflowWrap: 'anywhere',
  },
  '& td:first-of-type': { fontWeight: 700 },
  '& td p, & th p': { m: 0 },
  '& hr': { my: 3, borderColor: 'divider' },
};

const markdownComponents = {
  table: ({ node, ...props }: any) => (
    <Box sx={{ width: '100%', overflowX: 'auto', mb: 3 }}>
      <table {...props} />
    </Box>
  ),
};

export enum LegalContentType {
  TermsOfService = 'terms-of-service',
  PrivacyPolicy = 'privacy-policy',
}

const LegalContent: React.FC<{ type: LegalContentType; date?: string; language?: string }> = ({
  type,
  date,
  language,
}) => {
  const { t } = useTranslation();
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const userLanguage = useAppSelector((state) => selectUser(state)).data.language;
  const effectiveLanguage = language ?? userLanguage ?? Language.ENGLISH;

  useEffect(() => {
    setMarkdown(null);
    setError(false);

    const loadFile = async () => {
      try {
        const response = await fetch('/legal/manifest.json');
        if (!response.ok) throw new Error();

        const manifest = await response.json();
        const entries: { date: string; files: { [language: string]: string } }[] = manifest[type];

        if (entries.length === 0) throw new Error();

        let file: string | undefined;

        if (date) {
          const entry = entries.find((e) => e.date === date);
          if (!entry) throw new Error();
          file = entry.files[effectiveLanguage] ?? entry.files[Language.ENGLISH];
        } else {
          const newestLanguageEntry = entries
            .filter((e) => e.files[effectiveLanguage])
            .sort((a, b) => b.date.localeCompare(a.date))[0];

          const newestEnglishEntry = entries
            .filter((e) => e.files[Language.ENGLISH])
            .sort((a, b) => b.date.localeCompare(a.date))[0];

          file =
            newestLanguageEntry?.files[effectiveLanguage] ??
            newestEnglishEntry?.files[Language.ENGLISH];
        }

        if (!file) throw new Error();

        const textResponse = await fetch(`/legal/${file}`);
        if (!textResponse.ok) throw new Error();

        const text = await textResponse.text();
        if (text.trimStart().startsWith('<!DOCTYPE')) throw new Error(); // need to check this to avoid fall back to CRA behavior

        setMarkdown(text);
      } catch (error) {
        setError(true);
      }
    };
    loadFile();
  }, [type, date, effectiveLanguage]);

  return (
    <React.Fragment>
      <Container maxWidth="md" sx={{ py: 2 }}>
        {error ? (
          <CustomAlert severity="error" header={t('error-loading-content')} />
        ) : markdown === null ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CustomLoader />
          </Box>
        ) : (
          <Box sx={markdownStyles}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {markdown}
            </ReactMarkdown>
          </Box>
        )}
      </Container>
    </React.Fragment>
  );
};

export default LegalContent;
